import type { CampaignEmailFormValues } from '../types/campaign-email.types';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export type CampaignEmailFormErrors = Partial<Record<
| 'name'
| 'clientId'
| 'selectedCampaignIds'
| 'recipients'
| 'sectionsConfig'
| 'customPromptInstructions'
| 'brandingOverride'
| 'manualRecipients'
| 'dayOfWeek'
| 'startDate'
| 'endDate',
string
>>;

export function validateCampaignEmailForm(values: CampaignEmailFormValues): CampaignEmailFormErrors {
  const errors: CampaignEmailFormErrors = {};

  if (!values.name.trim()) {
    errors.name = 'Schedule name is required.';
  }

  if (values.dayOfWeek == null || values.dayOfWeek < 0 || values.dayOfWeek > 6) {
    errors.dayOfWeek = 'Select a day of the week.';
  }

  if (values.selectedCampaignIds.length === 0) {
    errors.selectedCampaignIds = 'Select at least one campaign.';
  }

  const hasPortalContacts = values.portalContactIds.length > 0;
  const validManualRecipients = values.manualRecipients.filter(
    (recipient) => recipient.email.trim() !== ''
  );

  if (!hasPortalContacts && validManualRecipients.length === 0) {
    errors.recipients = 'Add at least one portal contact or manual recipient.';
  }

  const invalidManual = values.manualRecipients.find(
    (recipient) => recipient.email.trim() !== '' && !EMAIL_PATTERN.test(recipient.email.trim())
  );
  if (invalidManual) {
    errors.manualRecipients = `Invalid email address: ${invalidManual.email}`;
  }

  if (values.sectionsConfig.summary_metrics.length === 0) {
    errors.sectionsConfig = 'Select at least one summary metric.';
  }

  if (values.frequency === 'daily') {
    if (!values.startDate) {
      errors.startDate = 'Start date is required for daily schedules.';
    }
    if (!values.endDate) {
      errors.endDate = 'End date is required for daily schedules.';
    }
    if (values.startDate && values.endDate && values.startDate > values.endDate) {
      errors.endDate = 'End date must be on or after the start date.';
    }
  }

  if (values.aiNarrativeEnabled && values.customPromptInstructions.trim().length > 2000) {
    errors.customPromptInstructions = 'Custom instructions must be 2000 characters or fewer.';
  }

  if (values.useBrandingOverride && values.brandingOverride.trim()) {
    try {
      // eslint-disable-next-line no-new
      new URL(values.brandingOverride.trim());
    } catch {
      errors.brandingOverride = 'Enter a valid branding URL.';
    }
  }

  return errors;
}

export function hasCampaignEmailFormErrors(errors: CampaignEmailFormErrors): boolean {
  return Object.keys(errors).length > 0;
}
